import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "@/hooks/useTheme";

interface NavbarProps {
  onBookCall: () => void;
}

const Navbar = ({ onBookCall }: NavbarProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { isDark, toggle } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Services", path: "/services" },
    { label: "Industries", path: "/industries" },
    { label: "Case Studies", path: "/case-studies" },
    { label: "Contact", path: "/contact" },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "nav-glass" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="https://public.readdy.ai/ai/img_res/371b58ef-9b22-4c45-8493-dbb40c63d635.png"
              alt="Blue Digital AI"
              className="w-[90px] h-[90px] object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isActive(link.path)
                    ? "text-sapphire"
                    : scrolled
                    ? "text-[#1d1d1f] dark:text-white/80 hover:text-sapphire dark:hover:text-white"
                    : "text-white hover:text-white/70"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA + Theme Toggle */}
          <div className="hidden md:flex items-center gap-3">
            {/* Day/Night Toggle */}
            <button
              onClick={toggle}
              aria-label="Toggle theme"
              className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer ${
                scrolled
                  ? "bg-[#f5f5f7] dark:bg-white/10 text-[#1d1d1f] dark:text-white hover:bg-[#e5e5e7] dark:hover:bg-white/20"
                  : "bg-white/10 text-white hover:bg-white/20"
              }`}
            >
              <i className={`${isDark ? "ri-sun-line" : "ri-moon-line"} text-base`}></i>
            </button>

            <button
              onClick={onBookCall}
              className="bg-sapphire hover:bg-deep-sapphire text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              Book a Strategy Call
            </button>
          </div>

          {/* Mobile right side */}
          <div className="md:hidden flex items-center gap-2">
            {/* Theme toggle mobile */}
            <button
              onClick={toggle}
              aria-label="Toggle theme"
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-all cursor-pointer ${
                scrolled
                  ? "bg-[#f5f5f7] dark:bg-white/10 text-[#1d1d1f] dark:text-white"
                  : "bg-white/10 text-white"
              }`}
            >
              <i className={`${isDark ? "ri-sun-line" : "ri-moon-line"} text-base`}></i>
            </button>

            <button
              className={`cursor-pointer ${scrolled ? "text-charcoal dark:text-white" : "text-white"}`}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <i className={`ri-${menuOpen ? "close" : "menu"}-line text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden bg-white dark:bg-[#1a1a1a] border-t border-gray-100 dark:border-white/10 py-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMenuOpen(false)}
                className={`block px-4 py-3 text-sm font-medium transition-colors cursor-pointer ${
                  isActive(link.path)
                    ? "text-sapphire"
                    : "text-charcoal dark:text-white/80 hover:text-sapphire"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="px-4 pt-2">
              <button
                onClick={() => { setMenuOpen(false); onBookCall(); }}
                className="w-full bg-sapphire hover:bg-deep-sapphire text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
              >
                Book a Strategy Call
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
